import React from "react";
import InViewReveal from "./InViewReveal";
import SectionWatermark from "./SectionWatermark";
import CatalogueCTA from "./CatalogueCTA";

/**
 * About / Brand statement - the editorial column.
 * Layout: pull-quote on the left (italic, vermilion left-rule), drop-cap
 * paragraph on the right, signed at the bottom in a brass tone.
 *
 * Watermark: monogram seal floats behind the right column. Heritage cue
 * (deeds, share certificates). Reinecke/Tuch: low VC + high PT = trust.
 */
export default function BrandStatement() {
  return (
    <section id="about" className="brand-statement-section relative paper" style={{ overflow: "hidden" }}>
      <SectionWatermark variant="monogram" position="top-right" size={420} opacity={0.18} />
      <div className="relative px-6 sm:px-10 py-14 md:py-20" style={{ zIndex: 1 }}>

        <InViewReveal className="flex items-baseline gap-4 mb-8 md:mb-12">
          <span className="folio">№ 03</span>
          <span className="rule rule-grow" style={{ flex: 1, height: 1 }} />
          <span className="folio" style={{ color: "var(--ink-mute)" }}>THE HOUSE</span>
        </InViewReveal>

        <div className="grid md:grid-cols-12 gap-10 md:gap-12">

          {/* Left - pull quote */}
          <InViewReveal delay={2} className="md:col-span-5">
            <div className="founders-note-panel paper">
              <div className="founders-note-kicker">
                FROM THE FOUNDERS' NOTE
              </div>
              <div className="founders-note-quote" aria-hidden="true">“</div>
              <div className="pull-quote founders-pull-quote">
                We do not <em>sell</em> goods.
                <br />
                We move it carefully, between two people who already trust each other.
              </div>
              <div className="founders-note-footer">
                <span className="folio-chip folio-chip--brass">FOLIO · 02</span>
                <span className="mono">HAND-WEIGHED · PORT-CLEARED</span>
              </div>
            </div>

            {/* Marginalia - a tiny editorial sidebar */}
            <aside className="marginalia-card mt-8 paper relative">
              <div className="marginalia-label">
                MARGINALIA
              </div>
              {/* TEMP: warehouse/port photo placeholders hidden until real images land.
              <div className="marginalia-proof-row" aria-hidden="true">
                <span>WAREHOUSE PHOTO</span>
                <span>PORT PHOTO</span>
              </div>
              */}
              <p>
                A typical Vilora container is photographed twice - once at the warehouse, once at port. Both photographs ride with the bill of lading. <em>Always.</em>
              </p>
            </aside>
          </InViewReveal>

          {/* Right - long-form copy with drop cap */}
          <InViewReveal delay={3} className="founders-copy-panel md:col-span-7 lg:col-span-6 lg:col-start-7">
            <p
              className="dropcap justify"
              style={{ fontSize: "clamp(18px, 1.35vw, 21px)", lineHeight: 1.68 }}
            >
              Vilora Impex began the way trading houses always have: a family,
              a sack of basmati, a buyer in Doha who needed it on Tuesday. A
              quarter century later the warehouses are larger, the manifests
              are digital, and the contracts come in eight currencies - but
              the work is the same. We grade by hand. We weigh twice. We
              stamp the seal ourselves.
            </p>

            <p style={{ marginTop: 22, fontSize: "clamp(17px, 1.25vw, 20px)", lineHeight: 1.68 }}>
              Out of Gujarat we move <em>premium long-grain</em> for the Gulf,
              <em> spice and masala</em> for the Levant, <em>dry fruit and pulses</em> for
              East Africa, and <em>fresh produce</em> on cold-chain to the Maldives. Each
              category sits with its own desk, its own buyer relationships, its
              own ledger of guarantees.
            </p>

            <div className="rule-engraved my-10" />

            <CatalogueCTA className="mb-10" code="EXPORT" compact />

            {/* Three signed pillars */}
            <div className="grid sm:grid-cols-3 gap-6">
              <Pillar n="i" title="Quality" body="Lab-tested, FSSAI/APEDA-aligned. Every lot, every shipment." />
              <Pillar n="ii" title="Logistics" body="Door-to-door across all major Indian ports. Cold-chain available." />
              <Pillar n="iii" title="Trust" body="Direct from origin. No middlemen, no off-spec lots, no surprises." />
            </div>

            {/* Signed off */}
            <div className="founders-signatures mt-12 grid sm:grid-cols-2 gap-5">
              <div className="founder-signature-card flex items-center gap-4">
                <div className="founder-initial" aria-hidden="true">D</div>
                <div>
                  <div className="display-italic founder-name" style={{ "--signature-delay": "0s" }}>D. Anadkat</div>
                  <div className="meta mt-1">FOUNDER · GUJARAT</div>
                </div>
              </div>
              <div className="founder-signature-card flex items-center gap-4">
                <div className="founder-initial" aria-hidden="true">H</div>
                <div>
                  <div className="display-italic founder-name" style={{ "--signature-delay": "1.6s" }}>H. Dudhatra</div>
                  <div className="meta mt-1">CO-FOUNDER · GUJARAT</div>
                </div>
              </div>
            </div>
          </InViewReveal>
        </div>
      </div>
    </section>
  );
}

function Pillar({ n, title, body }) {
  return (
    <div className="founder-pillar">
      <div className="display-italic" style={{ color: "var(--brass)", fontSize: 28, lineHeight: 1 }}>{n}.</div>
      <div className="display mt-2" style={{ fontSize: 20, fontWeight: 500 }}>{title}</div>
      <div className="rule my-3" style={{ width: 28 }} />
      <p style={{ fontSize: 15.5, lineHeight: 1.6, color: "var(--ink-soft)" }}>{body}</p>
    </div>
  );
}
